/**
 * A preview grown from its wall card returns into it (Part 10 Rule 3). Only
 * the person's closes play it: Esc, the backdrop, Back and the hover pill's
 * Back and ✕ run through `closeToCard`; every other end of a presentation
 * stays immediate. The presentation store ends at once, so this window keeps
 * the closing presentation here until its window's spring settles
 * (`components/focus/use-card-grow.ts`), and the wall keeps the card's slot
 * empty until then. Local to this window: the store is not mirrored.
 */
import { create } from "zustand";
import type { PresentationState } from "@/lib/focus/focus-types";
import { isFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";
import { usePresentationStore } from "@/stores/presentation-store";

interface CardReturnState {
  /** The presentation drawn while its window returns into its card. */
  closing: PresentationState | null;
}

const useCardReturnStore = create<CardReturnState>(() => ({ closing: null }));

/** The frame a presentation grew from its wall card, if it did. */
export function grownFrameId(
  presentation: PresentationState | null,
): string | null {
  return presentation?.motion?.kind === "from-card"
    ? presentation.motion.frameId
    : null;
}

/**
 * Runs a person's close. When it ended a presentation shown here that grew
 * from a wall card, that presentation keeps drawing while it returns.
 */
export function closeToCard(close: () => void): void {
  const before = usePresentationStore.getState().presentation;
  const shownHere = isFocusVisibleHere();
  close();
  if (
    shownHere &&
    grownFrameId(before) &&
    !usePresentationStore.getState().presentation
  ) {
    useCardReturnStore.setState({ closing: before });
  }
}

/** The return settled, or cannot play: the window goes, the card shows. */
export function finishCardReturn(): void {
  if (useCardReturnStore.getState().closing) {
    useCardReturnStore.setState({ closing: null });
  }
}

/** The presentation returning into its card here, if any. */
export function useClosingPresentation(): PresentationState | null {
  return useCardReturnStore((state) => state.closing);
}

/** The card whose window is returning into it here, if any. */
export function useCardReturnFrameId(): string | null {
  return useCardReturnStore((state) => grownFrameId(state.closing));
}

// A new presentation replaces a returning one at once.
usePresentationStore.subscribe((state) => {
  if (state.presentation) finishCardReturn();
});
